/**
 * Canonical API base URL for the app.
 * Should point to the Netlify Functions API endpoint (e.g. http://localhost:4000 or https://api.clumse.com/.netlify/functions/api).
 * Set in .env as REACT_APP_API_URL.
 *
 * Production builds must set REACT_APP_API_URL to the hosted Netlify Functions endpoint.
 */
const trimTrailingSlash = (url) => String(url || "").replace(/\/$/, "");

/**
 * Local Express serves routes at `/conversations`, not `/api/conversations`.
 * Some `.env` copies use `http://localhost:4000/api`, which 404s against a
 * plain `app.listen(4000)` backend. Netlify URLs like `.../.netlify/functions/api`
 * must keep their path - only strip a **standalone** `/api` path on localhost.
 */
function normalizeApiBaseForClient(url) {
  const trimmed = trimTrailingSlash(url);
  if (!trimmed) return trimmed;
  try {
    const u = new URL(trimmed);
    const local =
      u.hostname === "localhost" ||
      u.hostname === "127.0.0.1" ||
      u.hostname === "[::1]";
    if (local && u.pathname === "/api") {
      return trimTrailingSlash(`${u.origin}`);
    }
  } catch {
    /* ignore parse errors */
  }
  return trimmed;
}

export function getApiUrl() {
  const raw = normalizeApiBaseForClient(
    trimTrailingSlash(process.env.REACT_APP_API_URL || "")
  );
  if (process.env.NODE_ENV === "production") {
    if (!raw) {
      console.error(
        "[Clumse] REACT_APP_API_URL is not set. This build cannot reach the TypeScript API."
      );
    } else if (/localhost|127\.0\.0\.1/i.test(raw)) {
      console.error(
        "[Clumse] REACT_APP_API_URL points at localhost in a production build. Use your hosted API (e.g. https://api.clumse.com/.netlify/functions/api)."
      );
    }
  }
  return raw || "http://localhost:4000";
}

/**
 * Base for legacy Express handlers mounted at `/api/...` (sendChat, doesUserExist,
 * `/api/maintenance`, `/api/onboarding`, etc.).
 *
 * `getApiUrl()` is the **server root** on local dev (e.g. `http://localhost:4000`) so
 * `/conversations` and `/visual-visioning` resolve. Those routes still need `/api`
 * prepended for the older `/api/sendChat`-style mounts. Hosted Netlify bases already
 * end with `.../api` or `.../.netlify/functions/api` - return them unchanged.
 */
export function getExpressApiBaseUrl() {
  const root = trimTrailingSlash(getApiUrl());
  if (!root) return root;
  try {
    const u = new URL(root);
    const host = u.hostname.toLowerCase();
    const local =
      host === "localhost" || host === "127.0.0.1" || host === "[::1]";
    const path = (u.pathname || "").replace(/\/$/, "") || "";
    const alreadyApi =
      path === "/api" ||
      path.endsWith("/api") ||
      path.includes("/.netlify/functions/api");
    if (local && !alreadyApi) {
      return `${trimTrailingSlash(`${u.origin}`)}/api`;
    }
  } catch {
    /* ignore parse errors */
  }
  return root;
}

/**
 * Base URL for Stripe PaymentIntents, rent pay, Connect, etc.
 * In **production**, throws if REACT_APP_API_URL is missing or still localhost so
 * the app never silently talks to a dev server or empty host.
 */
export function getPaymentApiUrl() {
  const raw = normalizeApiBaseForClient(
    trimTrailingSlash(process.env.REACT_APP_API_URL || "")
  );
  if (process.env.NODE_ENV === "production") {
    if (!raw) {
      throw new Error(
        "Payments are disabled: set REACT_APP_API_URL to your production API base, e.g. https://api.clumse.com/.netlify/functions/api"
      );
    }
    if (/localhost|127\.0\.0\.1/i.test(raw)) {
      throw new Error(
        "Payments are disabled: REACT_APP_API_URL must not use localhost in production. Use the hosted TypeScript backend."
      );
    }
  }
  return raw || "http://localhost:4000";
}
